import React from 'react';
import { noop } from 'utils/noop';
import { SemanticUI } from '@iterable/deprecated_react_components';
import { determineRangeColors, getNumberRangeTheme, numberRangeThemes } from 'utils/themes';
import { Popover, PopoverTrigger } from 'ui/popover';
import { spacing } from 'ui/theme';
import {
  NumberRangeThemeContainer,
  MinMaxContainer,
  StyledCoreNumberInput,
  FooterBar,
  MenuHeader,
  MenuItem,
  SelectedIconContainer,
  GradientContainer,
  StyledPopoverContent,
  TriggerButton,
} from './NumberRangeThemePicker.styled';

// type Props = {
//   /** Additional class. */
//   className: string,
//   /** If custom theme is used instead of presets. */
//   customTheme: boolean,
//   /** If selector is disabled. */
//   disabled: boolean,
//   /** The min value of the range. */
//   min: number,
//   /** The max value of the range. */
//   max: number,
//   /** Callback function when theme changes. */
//   onChange: (e: {}, props: {}) => void,
//   /** Callback function when min or max values change. */
//   onChangeMinMax: (e: {}, values: {}) => void,
//   /** If the theme colors are reversed. */
//   reverse: boolean,
//   /** Show the min / max input boxes. */
//   showMinMax: boolean,
//   /** The name of the current theme. */
//   value: string,
// };

export default class NumberRangeThemePicker extends React.Component {
  static defaultProps = {
    onChange: noop,
    onChangeMinMax: noop,
    showMinMax: true,
  };

  constructor(props) {
    super(props);
    this.state = {
      open: false,
    };
  }

  /**
   * createThemeItems
   * Generates the menu items in the popup.
   * @param { String } currentTheme: The name of the current theme.
   * @return { Array } The menu contents.
   */
  createThemeItems = (currentTheme) => {
    const items = [];
    let currentThemeType = '';
    Object.keys(numberRangeThemes).forEach((themeName, key) => {
      const theme = getNumberRangeTheme(themeName);
      const { name, type } = theme;
      if (type !== currentThemeType) {
        currentThemeType = type;
        items.push(<MenuHeader key={`menu-header-${key}`}>{type}</MenuHeader>);
      }
      items.push(
        <MenuItem key={`menu-item-${key}`} onClick={this.handleClickItem} value={name}>
          {this.createThemeGradient(theme)}
          {name === currentTheme && (
            <SelectedIconContainer>
              <SemanticUI.Icon name="check" />
            </SelectedIconContainer>
          )}
        </MenuItem>,
      );
    });
    return items;
  };

  /**
   * createThemeGradient
   * Generates the gradient boxes using the theme colors.
   * @param { Object } theme: The theme definition containing the min, mod and max colors.
   * @return { React.Component } The gradient boxes.
   */
  createThemeGradient = (theme) => {
    const { reverse } = this.props;
    const { min, max, mid } = theme || {};
    const { minColor, midMinColor, midColor, midMaxColor, maxColor } = determineRangeColors(
      min,
      max,
      mid,
      reverse,
    );
    return (
      <GradientContainer data-test="theme-gradient-container">
        <div style={{ background: minColor }} />
        <div style={{ background: midMinColor }} />
        <div style={{ background: midColor }} />
        <div style={{ background: midMaxColor }} />
        <div style={{ background: maxColor }} />
      </GradientContainer>
    );
  };

  /**
   * handleBlur
   * Callback function when number input is blurred. Ensures the max value is always larger.
   * @param { Object } e: The blur event.
   * @returns { void }
   */
  handleBlur = (e) => {
    const { min, max, onChangeMinMax } = this.props;
    onChangeMinMax(e, { min: Math.min(min, max), max: Math.max(min, max) });
  };

  /**
   * handleClickItem
   * Callback function when theme item is clicked.
   * @param { Object } e: The change event.
   * @param { Object } props: The props of the theme item clicked.
   * @returns { void }
   */
  handleClickItem = (e, { value }) => {
    const { onChange, reverse } = this.props;
    const theme = getNumberRangeTheme(value);
    onChange(e, { ...theme, reverse, value });
    this.handleClose();
  };

  /**
   * handleChangeMin
   * Callback function when the min value is changed.
   * @param { Object } e: The change event.
   * @param { Object } props: The props of the number input containing the updated min value.
   * @returns { void }
   */
  handleChangeMin = (e, { value: min }) => {
    const { max, onChangeMinMax } = this.props;
    onChangeMinMax(e, { min, max });
  };

  /**
   * handleChangeMax
   * Callback function when the max value is changed.
   * @param { Object } e: The change event.
   * @param { Object } props: The props of the number input containing the updated max value.
   * @returns { void }
   */
  handleChangeMax = (e, { value: max }) => {
    const { min, onChangeMinMax } = this.props;
    onChangeMinMax(e, { min, max });
  };

  /**
   * handleClose
   * Callback function when the popup is closed.
   * @returns { void }
   */
  handleClose = () => {
    this.setState({ open: false });
  };

  /**
   * handleOpen
   * Callback function when the popup is opened.
   * @returns { void }
   */
  handleOpen = () => {
    this.setState({ open: true });
  };

  /**
   * handleChangeReverse
   * Callback function the toggle for the reverse value is changed.
   * @param { Object } e: The change event.
   * @param { Object } props: The props of the toggle containing the updated reverse value.
   * @returns { void }
   */
  handleChangeReverse = (e, { checked }) => {
    const { onChange, value } = this.props;
    onChange(e, { value, reverse: checked });
  };

  render() {
    const { className, disabled, min, max, reverse, showMinMax, value } = this.props;
    const disableIncrement = min === max - 1;

    return (
      <NumberRangeThemeContainer className={className}>
        <Popover>
          <StyledPopoverContent>
            <SemanticUI.Menu>
              {this.createThemeItems(value)}
              <FooterBar>
                <SemanticUI.Checkbox
                  label={'Reverse Colors'}
                  toggle
                  checked={reverse}
                  onChange={this.handleChangeReverse}
                />
              </FooterBar>
            </SemanticUI.Menu>
          </StyledPopoverContent>
          <PopoverTrigger>
            <TriggerButton disabled={disabled}>
              {this.createThemeGradient(getNumberRangeTheme(value))}
              <SemanticUI.Icon name="dropdown" />
            </TriggerButton>
          </PopoverTrigger>
        </Popover>
        {showMinMax && (
          <MinMaxContainer>
            <div>
              <label style={{ marginRight: spacing.half }}>Min</label>
              <StyledCoreNumberInput
                disableIncrement={disableIncrement}
                onBlur={this.handleBlur}
                onChange={this.handleChangeMin}
                value={min}
              />
            </div>
            <div>
              <label style={{ marginRight: spacing.half }}>Max</label>
              <StyledCoreNumberInput
                disableDecrement={disableIncrement}
                onBlur={this.handleBlur}
                onChange={this.handleChangeMax}
                value={max}
              />
            </div>
          </MinMaxContainer>
        )}
      </NumberRangeThemeContainer>
    );
  }
}
